import React, { useState, useEffect } from 'react';
import Button from './Button';
import { Badge } from './Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { DEFAULT_SYSTEM_INSTRUCTIONS, MODELS } from '../constants';
import { generateStudentReportDocx, buildReportFileName, hasExportableResult } from '../services/reportExportService';
import TextArea from './TextArea';
import { transcribeImage } from '../services/aiService';
import { extractTextFromFile, deleteModelResult } from '../services/assessmentService';
import { ModelResult } from '../src/types';
import { cn } from '../lib/utils';
import { 
  ArrowLeft, 
  Download, 
  RefreshCw, 
  Plus, 
  FileText, 
  User, 
  Bot, 
  ChevronDown, 
  ChevronUp,
  Trash2,
  Camera,
  Upload,
  FileUp,
  Info,
  CheckCircle,
  AlertCircle,
  Clock,
  Hash,
  BookOpen,
  PenTool,
  Sparkles,
  Image,
  MessageSquare,
  Target,
  BarChart3,
  Save,
  HelpCircle
} from 'lucide-react';

// Helper to trigger browser download
const saveDocumentToFile = (blob, fileName) => {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const HighlightedText = ({ text, highlight }) => {
  if (!text) return null;
  // Regex to safely split by the highlight string, including the parentheses.
  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} className="font-bold text-red-500 bg-red-100 rounded px-1 py-0.5" title="AI flagged this as a potential factual inaccuracy for review.">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};


const MarkdownRenderer = ({ text }) => {
    if (!text || typeof text !== 'string') return null;

    const lines = text.split('\n');

    const elements = lines.map((line, index) => {
        // Headings
        if (line.startsWith('### ')) {
            return <h3 key={index} className="text-xl font-semibold text-secondary-800 !mt-4 !mb-2">{line.substring(4)}</h3>;
        }
        // Blockquotes
        if (line.startsWith('> ')) {
            return <blockquote key={index} className="border-l-4 border-secondary-300 pl-4 italic text-secondary-700 my-2">{line.substring(2)}</blockquote>;
        }
        // Bullet points (and bolding within them)
        const bulletMatch = line.match(/^\s*\*\s+(.*)/);
        if (bulletMatch) {
            const content = bulletMatch[1];
            // Split by bold markdown, keeping the delimiters
            const parts = content.split(/(\*\*.*?\*\*)/g).filter(Boolean);
            const children = parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={i}>{part.slice(2, -2)}</strong>;
                }
                return part;
            });
            return <li key={index}>{children}</li>;
        }
        // Empty lines as paragraphs for spacing
        if (line.trim() === '') {
            return <p key={index}>&nbsp;</p>;
        }
        // Regular paragraph with bolding
        const parts = line.split(/(\*\*.*?\*\*)/g).filter(Boolean);
        const children = parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return part;
        });
        return <p key={index}>{children}</p>;
    });
    
    // Group <li> elements inside <ul>
    const finalElements = [];
    let listItems = [];
    elements.forEach((el, index) => {
        if (el.type === 'li') {
            listItems.push(el);
        } else {
            if (listItems.length > 0) {
                finalElements.push(<ul key={`ul-${index}`} className="list-disc space-y-2 ml-5">{listItems}</ul>);
                listItems = [];
            }
            finalElements.push(el);
        }
    });
    if (listItems.length > 0) {
        finalElements.push(<ul key="ul-final" className="list-disc space-y-2 ml-5">{listItems}</ul>);
    }

    return <div className="space-y-2">{finalElements}</div>;
};



const ProcessStepDivider = ({ text }: { text: string }) => (
    <div className="flex items-center justify-center my-6" aria-hidden="true">
        <svg className="h-6 w-6 text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
        </svg>
        <span className="ml-2 text-sm font-medium text-secondary-500 italic">{text}</span>
    </div>
);

// Helper function to normalize criterion names for comparison
const normalizeCriterionName = (name: string | undefined | null): string => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, ' ').trim();
};

// Progress bar component for scores
const ScoreProgressBar = ({ 
  score, 
  label, 
  variant = 'match' 
}: { 
  score: number; 
  label: string; 
  variant?: 'match' | 'confidence';
}) => {
  const getColorClasses = (score: number, variant: string) => {
    if (variant === 'confidence') {
      if (score >= 80) return 'bg-primary';
      if (score >= 60) return 'bg-primary/70';
      return 'bg-primary/50';
    }
    // Match variant
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium text-foreground">{score}%</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div 
          className={cn("h-full rounded-full transition-all duration-300", getColorClasses(score, variant))}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

const DetailedMarkingBreakdownSection = ({ modelResult, modelName, assessment, essayData }: { 
  modelResult: any; 
  modelName?: string; 
  assessment: any;
  essayData: any;
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [expandedCriteria, setExpandedCriteria] = useState<Set<number>>(new Set());
  const breakdownTitle = modelName ? `Detailed Marking Breakdown for ${modelName}` : 'Detailed Marking Breakdown';
  const breakdownModelName = modelName || 'the AI model';

  const toggleCriterion = (index: number) => {
    setExpandedCriteria(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Get the most recent marking run that has detailed band analysis
  const getDetailedMarkingData = () => {
    if (!modelResult.rawMarkingRuns || modelResult.rawMarkingRuns.length === 0) {
      return null;
    }
    
    const runWithBandAnalysis = modelResult.rawMarkingRuns
      .slice()
      .reverse()
      .find((run: any) => 
        run.criteriaResults && 
        run.criteriaResults.some((crit: any) => crit.bandAnalysis && crit.bandAnalysis.length > 0)
      );
    
    return runWithBandAnalysis;
  };

  const detailedRun = getDetailedMarkingData();

  if (!detailedRun || !detailedRun.criteriaResults) {
    return null;
  }

  const findTeacherManualResult = (criterionName: string) => {
    if (!essayData.manualMarkingResults || !Array.isArray(essayData.manualMarkingResults)) {
      return null;
    }
    const normalizedName = normalizeCriterionName(criterionName);
    return essayData.manualMarkingResults.find((manual: any) => 
      normalizeCriterionName(manual.criterionName) === normalizedName
    );
  };

  const getAllBandsForCriterion = (rubricCriterion: any, aiBandAnalysis: any[]) => {
    const allBands: any[] = [];
    
    if (rubricCriterion && rubricCriterion.bands && Array.isArray(rubricCriterion.bands)) {
      rubricCriterion.bands.forEach((rb: any) => {
        const bandName = `${rb.min}-${rb.max}`;
        const aiAnalysis = aiBandAnalysis?.find((ab: any) => {
          if (!ab.bandName) return false;
          if (ab.bandName === bandName) return true;
          const match = ab.bandName.match(/(\d+)\s*[-–]\s*(\d+)/);
          if (match) {
            return parseInt(match[1], 10) === rb.min && parseInt(match[2], 10) === rb.max;
          }
          return false;
        });
        
        allBands.push({
          bandName,
          min: rb.min,
          max: rb.max,
          descriptor: rb.descriptor || '',
          matchScore: aiAnalysis?.matchScore ?? null,
          confidenceScore: aiAnalysis?.confidenceScore ?? null,
          hasAiAnalysis: !!aiAnalysis
        });
      });
    }
    
    if (aiBandAnalysis && Array.isArray(aiBandAnalysis)) {
      aiBandAnalysis.forEach((ab: any) => {
        if (!ab.bandName) return;
        const match = ab.bandName.match(/(\d+)\s*[-–]\s*(\d+)/);
        if (match) {
          const abMin = parseInt(match[1], 10);
          const abMax = parseInt(match[2], 10);
          const exists = allBands.some(b => b.min === abMin && b.max === abMax);
          if (!exists) {
            allBands.push({
              bandName: ab.bandName,
              min: abMin,
              max: abMax,
              descriptor: '',
              matchScore: ab.matchScore ?? null,
              confidenceScore: ab.confidenceScore ?? null,
              hasAiAnalysis: true
            });
          }
        }
      });
    }
    
    return allBands.sort((a, b) => (b.max || 0) - (a.max || 0));
  };

  return (
    <Card className="overflow-hidden">
      {/* Collapsible Header */}
      <button
        onClick={() => setShowBreakdown(!showBreakdown)}
        className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors text-left"
        aria-expanded={showBreakdown}
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-foreground text-sm">{breakdownTitle}</h4>
            <p className="text-xs text-muted-foreground">
              View how {breakdownModelName} evaluated each band descriptor
            </p>
          </div>
        </div>
        <ChevronDown className={cn(
          "h-5 w-5 text-muted-foreground transition-transform duration-200",
          showBreakdown && "rotate-180"
        )} />
      </button>

      {/* Expandable Content */}
      {showBreakdown && (
        <div className="border-t">
          <CardContent className="p-4 space-y-4">
            {detailedRun.criteriaResults.map((criterion: any, index: number) => {
              if (!criterion.bandAnalysis || criterion.bandAnalysis.length === 0) {
                return null;
              }

              const rubricCriterion = assessment?.criteria?.find((c: any) => 
                normalizeCriterionName(c.name) === normalizeCriterionName(criterion.criterionName)
              );
              const teacherManualResult = findTeacherManualResult(criterion.criterionName);
              const allBands = getAllBandsForCriterion(rubricCriterion, criterion.bandAnalysis);
              const isExpanded = expandedCriteria.has(index);

              return (
                <Card key={index} className="overflow-hidden border-border">
                  {/* Criterion Header */}
                  <div className="bg-secondary/30 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h5 className="font-semibold text-foreground">{criterion.criterionName}</h5>
                          {rubricCriterion && (
                            <Badge variant="outline" className="text-xs">
                              Weight: {rubricCriterion.weight}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* AI vs Teacher Comparison */}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="bg-background rounded-lg p-3 border">
                        <p className="text-[10px] font-medium uppercase tracking-wider text-primary mb-2 flex items-center gap-1">
                          <Bot className="h-3 w-3" />
                          AI Selected
                        </p>
                        <div className="flex items-baseline gap-2">
                          <Badge variant="default" className="text-sm px-2 py-0.5">
                            {criterion.selectedBand}
                          </Badge>
                          {criterion.score !== undefined && (
                            <span className="text-sm text-muted-foreground">
                              Score: <span className="font-medium text-foreground">{criterion.score}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className={cn(
                        "rounded-lg p-3 border-2",
                        teacherManualResult?.band 
                          ? "bg-green-50 border-green-200" 
                          : "bg-secondary/50 border-dashed border-border"
                      )}>
                        <p className={cn(
                          "text-[10px] font-medium uppercase tracking-wider mb-2 flex items-center gap-1",
                          teacherManualResult?.band ? "text-green-700" : "text-muted-foreground"
                        )}>
                          <User className="h-3 w-3" />
                          Your Selection
                        </p>
                        {teacherManualResult?.band ? (
                          <Badge variant="outline" className="text-sm px-2 py-0.5 bg-green-100 text-green-700 border-green-300">
                            {teacherManualResult.band}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Not marked</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Band Analysis Toggle */}
                  <button
                    onClick={() => toggleCriterion(index)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/30 transition-colors border-t text-left"
                  >
                    <span className="text-sm font-medium text-foreground">
                      AI Evaluation Against Each Band
                    </span>
                    <ChevronDown className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform",
                      isExpanded && "rotate-180"
                    )} />
                  </button>

                  {/* Band Details */}
                  {isExpanded && (
                    <div className="border-t bg-background">
                      <div className="p-4 space-y-3">
                        {allBands.map((band: any, bandIndex: number) => {
                          const isSelectedBand = (() => {
                            if (!criterion.selectedBand) return false;
                            if (band.bandName === criterion.selectedBand) return true;
                            const match = criterion.selectedBand.match(/(\d+)\s*[-–]\s*(\d+)/);
                            if (match) {
                              return parseInt(match[1], 10) === band.min && parseInt(match[2], 10) === band.max;
                            }
                            return false;
                          })();
                          
                          const hasAiScores = band.matchScore !== null && band.confidenceScore !== null;
                          
                          return (
                            <div 
                              key={bandIndex} 
                              className={cn(
                                "rounded-lg border p-4 transition-all",
                                isSelectedBand 
                                  ? "border-green-300 bg-green-50/50 ring-1 ring-green-200" 
                                  : hasAiScores
                                    ? "border-border bg-card"
                                    : "border-dashed border-border bg-secondary/20"
                              )}
                            >
                              {/* Band Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <span className={cn(
                                    "font-semibold text-sm",
                                    isSelectedBand ? "text-green-700" : "text-foreground"
                                  )}>
                                    {band.bandName} marks
                                  </span>
                                  {isSelectedBand && (
                                    <Badge className="bg-green-500 text-white text-[10px] px-2 py-0">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      AI SELECTED
                                    </Badge>
                                  )}
                                </div>
                              </div>

                              {/* Score Bars */}
                              {hasAiScores ? (
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                  <ScoreProgressBar 
                                    score={band.matchScore} 
                                    label="AI Match" 
                                    variant="match"
                                  />
                                  <ScoreProgressBar 
                                    score={band.confidenceScore} 
                                    label="AI Confidence" 
                                    variant="confidence"
                                  />
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic mb-3">
                                  AI did not analyze this band
                                </p>
                              )}

                              {/* Band Descriptor */}
                              <div className={cn(
                                "rounded-md p-3 text-sm",
                                isSelectedBand 
                                  ? "bg-green-100/50 border border-green-200" 
                                  : "bg-secondary/50 border border-border"
                              )}>
                                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
                                  Band Descriptor
                                </p>
                                <p className={cn(
                                  "text-sm leading-relaxed",
                                  isSelectedBand ? "text-green-800" : "text-foreground"
                                )}>
                                  {band.descriptor || (
                                    <span className="italic text-muted-foreground">
                                      No descriptor available
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Justifications */}
                      <div className="px-4 pb-4 space-y-3">
                        {criterion.justification && (
                          <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                            <div className="flex items-start gap-2">
                              <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                                  AI Justification for Selected Band
                                </p>
                                <p className="text-sm text-foreground leading-relaxed">
                                  {criterion.justification}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {criterion.justificationBandAbove && (
                          <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-1">
                                  Why Higher Band Was NOT Selected
                                </p>
                                <p className="text-sm text-orange-800 leading-relaxed">
                                  {criterion.justificationBandAbove}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {criterion.justificationBandBelow && (
                          <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-1">
                                  Why Lower Band Was NOT Selected
                                </p>
                                <p className="text-sm text-green-800 leading-relaxed">
                                  {criterion.justificationBandBelow}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </CardContent>
        </div>
      )}
    </Card>
  );
};

// Props interface for AIMarkingResultCard
interface AIMarkingResultCardProps {
  modelId: string;
  modelResult: ModelResult;
  modelConfig: any;
  displayModelName: string;
  lastRun: string;
  isAdmin: boolean;
  showManualSections: boolean;
  essayData: any;
  assessment: any;
  isProcessing: boolean;
  onDeleteModelResult?: (essayId: string, modelId: string) => Promise<void>;
  onExportReport?: (modelId: string) => void;
}

// AIMarkingResultCard - Displays a single AI model's marking results
const AIMarkingResultCard = ({
  modelId,
  modelResult,
  modelConfig,
  displayModelName,
  lastRun,
  isAdmin,
  showManualSections,
  essayData,
  assessment,
  isProcessing,
  onDeleteModelResult,
  onExportReport
}: AIMarkingResultCardProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(new Set());

  const toggleCriterion = (criterionName: string) => {
    setExpandedCriteria(prev => {
      const next = new Set(prev);
      if (next.has(criterionName)) {
        next.delete(criterionName);
      } else {
        next.add(criterionName);
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (onDeleteModelResult && window.confirm(`Delete ${displayModelName} results?`)) {
      await onDeleteModelResult(essayData.id, modelId);
    }
  };

  return (
    <div className="rounded-xl border bg-card overflow-hidden shadow-sm">
      {/* Model Header */}
      <div 
        className="flex items-center justify-between p-4 bg-secondary/30 cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Bot className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              {displayModelName}
              {modelResult.finalOverallBand && (
                <Badge variant="secondary" className="text-xs">
                  Band {modelResult.finalOverallBand}
                </Badge>
              )}
            </h3>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {lastRun}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Score Badge */}
          <div className="text-center px-4 py-2 rounded-lg bg-primary text-primary-foreground">
            <p className="text-2xl font-bold">{modelResult.finalOverallScore ?? 'N/A'}</p>
            <p className="text-[10px] uppercase tracking-wide opacity-80">Score</p>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            {onExportReport && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onExportReport(modelId); }}
                disabled={isProcessing}
                className="h-8 w-8 p-0"
                title="Export this marking as a feedback report (.docx)"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
            {isAdmin && onDeleteModelResult && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                disabled={isProcessing}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
              className="h-8 w-8 p-0"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t">
          {/* Metadata Row */}
          <div className="flex flex-wrap gap-2 text-xs">
            {modelResult.scoringMethodUsed && (
              <Badge variant="outline" className="gap-1">
                <BarChart3 className="h-3 w-3" />
                {modelResult.scoringMethodUsed}
              </Badge>
            )}
            {modelResult.rawMarkingRuns && (
              <Badge variant="outline" className="gap-1">
                <RefreshCw className="h-3 w-3" />
                {modelResult.rawMarkingRuns.length} run{modelResult.rawMarkingRuns.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {showManualSections && essayData.manualOverallScore !== undefined && (
              <Badge variant="outline" className="gap-1 bg-green-50 text-green-700 border-green-200">
                <CheckCircle className="h-3 w-3" />
                Manual: {essayData.manualOverallScore}
              </Badge>
            )}
          </div>

          {/* Criteria Breakdown */}
          {modelResult.consolidatedMarkingResults && modelResult.consolidatedMarkingResults.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Marking Breakdown
              </h4>
              <div className="space-y-2">
                {modelResult.consolidatedMarkingResults.map((criterion: any, idx: number) => {
                  const isOpen = expandedCriteria.has(criterion.criterionName);
                  const manualMark = essayData.manualMarkingResults?.find(
                    (m: any) => normalizeCriterionName(m.criterionName) === normalizeCriterionName(criterion.criterionName)
                  );
                  
                  return (
                    <div key={idx} className="rounded-lg border bg-card overflow-hidden">
                      {/* Criterion Header */}
                      <div 
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-secondary/30 transition-colors"
                        onClick={() => toggleCriterion(criterion.criterionName)}
                      >
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-sm text-foreground truncate">
                            {criterion.criterionName}
                          </h5>
                          <p className="text-xs text-muted-foreground">
                            AI: <span className="font-medium text-foreground">{criterion.selectedBand}</span>
                            {criterion.score !== undefined && (
                              <span className="ml-2">Score: <span className="font-medium text-foreground">{criterion.score}</span></span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {showManualSections && manualMark?.band && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              Teacher: {manualMark.band}
                            </Badge>
                          )}
                          {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </div>
                      </div>
                      
                      {/* Criterion Details */}
                      {isOpen && (
                        <div className="p-3 pt-0 space-y-3 border-t bg-secondary/10">
                          {criterion.justification && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">AI Justification</p>
                              <p className="text-sm text-foreground bg-background p-2 rounded border">
                                {criterion.justification}
                              </p>
                            </div>
                          )}
                          
                          {showManualSections && manualMark?.justification && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-green-700">Teacher Notes</p>
                              <p className="text-sm text-foreground bg-green-50 p-2 rounded border border-green-200">
                                {manualMark.justification}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Generated Feedback */}
          {modelResult.feedback && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                AI Feedback
              </h4>
              <div className="bg-secondary/30 rounded-lg p-4 border">
                <div className="prose prose-sm max-w-none">
                  <MarkdownRenderer text={modelResult.feedback} />
                </div>
              </div>
            </div>
          )}

          {/* Detailed Breakdown & Debug (Admin) */}
          <div className="space-y-2 pt-2">
            <DetailedMarkingBreakdownSection 
              modelResult={modelResult}
              modelName={displayModelName}
              assessment={assessment}
              essayData={essayData}
            />
            {isAdmin && (
              <ModelDebugSection 
                modelResult={modelResult} 
                modelName={displayModelName} 
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ModelDebugSection = ({ modelResult, modelName }: { modelResult: any; modelName: string }) => {
  const [showDebug, setShowDebug] = useState(false);

  return (
    <div className="border border-secondary-200 rounded-lg">
      <button
        onClick={() => setShowDebug(!showDebug)}
        className="w-full flex justify-between items-center p-3 bg-secondary-100 hover:bg-secondary-200 rounded-t-lg focus:outline-none text-left"
        aria-expanded={showDebug}
      >
        <h6 className="text-sm font-semibold text-secondary-700">
          Show {modelName} Debug Information
        </h6>
        <svg className={`w-4 h-4 text-secondary-600 transform transition-transform duration-200 ${showDebug ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {showDebug && (
        <div className="p-4 bg-white border-t border-secondary-200 rounded-b-lg space-y-4">
          {/* Marking Prompt */}
          {modelResult.markingPromptUsed && (
            <div>
              <h6 className="text-sm font-semibold text-primary-600 mb-2">Marking Prompt</h6>
              <pre className="whitespace-pre-wrap text-xs text-secondary-600 bg-secondary-50 p-3 rounded-md overflow-x-auto max-h-96">
                {modelResult.markingPromptUsed}
              </pre>
            </div>
          )}

          {/* Raw Marking Runs */}
          {modelResult.rawMarkingRuns && modelResult.rawMarkingRuns.length > 0 && (
            <div>
              <h6 className="text-sm font-semibold text-primary-600 mb-2">Raw Marking Runs</h6>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {modelResult.rawMarkingRuns.map((run: any, index: number) => (
                  <div key={index} className="p-2 border border-secondary-100 rounded-md bg-secondary-50 text-xs">
                    <p className="font-medium mb-1">Run {run.runNumber || index + 1}:</p>
                    {run.overallEssayScore && <p>Score: {run.overallEssayScore}</p>}
                    {run.overallEssayBand && <p>Band: {run.overallEssayBand}</p>}
                    {run.overallConfidenceForRun && <p>Confidence: {run.overallConfidenceForRun}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meta Analysis Prompt */}
          {modelResult.metaAnalysisPromptUsed && (
            <div>
              <div className="p-2 mb-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md">
                <strong>Meta-analysis was used for this model result</strong>
              </div>
              <h6 className="text-sm font-semibold text-primary-600 mb-2">Meta-Analysis Prompt</h6>
              <pre className="whitespace-pre-wrap text-xs text-secondary-600 bg-secondary-50 p-3 rounded-md overflow-x-auto max-h-96">
                {modelResult.metaAnalysisPromptUsed}
              </pre>
            </div>
          )}

          {/* Score Calculation Trace */}
          {modelResult.scoringCalculationTrace && (
            <div>
              <h6 className="text-sm font-semibold text-primary-600 mb-2">Score Calculation</h6>
              <pre className="whitespace-pre-wrap text-xs text-secondary-600 bg-secondary-50 p-3 rounded-md overflow-x-auto max-h-96">
                {modelResult.scoringCalculationTrace}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResultsDisplay = ({ essayData, assessment, onStartOver, onRemark, isProcessing, onBackToEssays, onSaveManualMarks, onExplainVariance, onDeleteModelResult, isAdmin = false, manualScoringEnabled = false }: {
  essayData: any;
  assessment: any;
  onStartOver: () => void;
  onRemark: () => void;
  isProcessing: boolean;
  onBackToEssays: () => void;
  onSaveManualMarks: (essayId: string, manualData: any) => Promise<void>;
  onExplainVariance: (essayData: any, teacherFeedback: string, selectedModelId?: string) => Promise<void>;
  onDeleteModelResult?: (essayId: string, modelId: string) => Promise<void>;
  isAdmin?: boolean;
  manualScoringEnabled?: boolean;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  
  const [manualInputs, setManualInputs] = useState<Record<string, any>>({});
  const [teacherFeedback, setTeacherFeedback] = useState('');
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [isExplaining, setIsExplaining] = useState(false);
  const [selectedModelForComparison, setSelectedModelForComparison] = useState('');
  
  const [isProcessingFeedbackFile, setIsProcessingFeedbackFile] = useState(false);
  const [feedbackFileError, setFeedbackFileError] = useState<string | null>(null);
  const showManualSections = isAdmin || manualScoringEnabled;
  const totalModels = essayData?.modelResults ? Object.keys(essayData.modelResults).length : 0;

  const getModelDisplayName = (modelId: string) => {
    if (!isAdmin) return 'AI Mark';
    return MODELS[modelId]?.name || modelId;
  };

  const formatModelLastRun = (value: any) => {
    if (!value) return 'Not available';
    try {
      if (value && typeof (value as any).toDate === 'function') {
        return (value as any).toDate().toLocaleString();
      }
      if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
        const ts = value as any;
        const date = new Date(ts.seconds * 1000 + ts.nanoseconds / 1_000_000);
        if (!isNaN(date.getTime())) return date.toLocaleString();
      }
      if (typeof value === 'string') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) return date.toLocaleString();
      }
      if (typeof value === 'number') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) return date.toLocaleString();
      }
      if (value instanceof Date && !isNaN(value.getTime())) {
        return value.toLocaleString();
      }
      return 'Date unavailable';
    } catch (error) {
      console.warn('Error parsing lastRunAt date:', value, error);
      return 'Date unavailable';
    }
  };

  useEffect(() => {
    const initialInputs: Record<string, any> = {};
    if (essayData.consolidatedMarkingResults) {
      essayData.consolidatedMarkingResults.forEach((crit: any) => {
        const manualCrit = essayData.manualMarkingResults?.find((mc: any) => mc.criterionName === crit.criterionName);
        initialInputs[crit.criterionName] = manualCrit?.band ?? '';
        initialInputs[`${crit.criterionName}-justification`] = manualCrit?.justification ?? '';
      });
    }
    initialInputs['overall'] = essayData.manualOverallScore ?? '';
    setManualInputs(initialInputs);
    setTeacherFeedback(essayData.teacherFeedback || '');
  }, [essayData]);

  const handleManualInputChange = (criterionName: string, value: any) => {
    setManualInputs(prev => ({ ...prev, [criterionName]: value }));
  };

  const handleSaveManual = async () => {
    setIsSavingManual(true);
    const criteriaPayload = (essayData.consolidatedMarkingResults || []).map((crit: any) => {
      const band = manualInputs[crit.criterionName] === '' || manualInputs[crit.criterionName] === null ? null : manualInputs[crit.criterionName];
      const justification = manualInputs[`${crit.criterionName}-justification`] === '' || manualInputs[`${crit.criterionName}-justification`] === null ? null : manualInputs[`${crit.criterionName}-justification`];
      
      return {
        criterionName: crit.criterionName,
        band,
        justification,
      };
    }).filter((c: any) => c.band !== null || c.justification !== null);
    
    const manualOverallScoreValue = manualInputs['overall'];
    const parsedScore = manualOverallScoreValue !== '' && manualOverallScoreValue !== null && manualOverallScoreValue !== undefined
      ? Number(manualOverallScoreValue)
      : null;

    const payload = {
      criteria: criteriaPayload,
      overallScore: parsedScore !== null && !Number.isNaN(parsedScore) ? parsedScore : null,
      teacherFeedback: teacherFeedback,
    };
    await onSaveManualMarks(essayData.id, payload);
    setIsSavingManual(false);
  };

  const handleExplainVarianceClick = async () => {
      setIsExplaining(true);
      
      // For multi-model view, pass the selected model's data
      if (isMultiModelView && selectedModelForComparison) {
        await onExplainVariance(essayData, teacherFeedback, selectedModelForComparison);
      } else {
        // For single model view or legacy behavior
        await onExplainVariance(essayData, teacherFeedback);
      }
      
      setIsExplaining(false);
  };

  const handleFeedbackFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessingFeedbackFile(true);
    setFeedbackFileError(null);

    try {
      let transcribedText = '';
      if (file.type.startsWith('image/')) {
        const base64String = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result.split(',')[1]);
            } else {
              reject(new Error('Failed to read file as a Base64 string.'));
            }
          };
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
        transcribedText = await transcribeImage(base64String);
      } else {
        transcribedText = await extractTextFromFile(file);
      }
      
      setTeacherFeedback(prev => {
        const separator = `\n\n--- Transcribed from ${file.name} ---\n\n`;
        return `${prev}${prev.trim() ? separator : ''}${transcribedText}`;
      });

    } catch (e: any) {
      setFeedbackFileError(e?.message || 'Failed to process file.');
    } finally {
      setIsProcessingFeedbackFile(false);
      // Reset input so the same file can be uploaded again
      if (event.target) {
        event.target.value = '';
      }
    }
  };


  const systemInstructionsToDisplay = assessment?.systemInstructions ?? DEFAULT_SYSTEM_INSTRUCTIONS;

  const handleDownloadWord = async (modelId?: string) => {
    if (!essayData) return;
    setIsDownloading(true);
    try {
        const blob = await generateStudentReportDocx(essayData, assessment, modelId);
        saveDocumentToFile(blob, buildReportFileName(essayData, assessment));
    } catch (e) {
        console.error("Error generating Word document:", e);
    } finally {
        setIsDownloading(false);
    }
  };

  // Determine if we should show single-model or multi-model interface
  const modelResultsCount = essayData.modelResults ? Object.keys(essayData.modelResults).length : 0;
  const isMultiModelView = modelResultsCount > 1;

  const renderRubric = () => {
    if (assessment?.criteria && Array.isArray(assessment.criteria) && assessment.criteria.length > 0) {
      return (
        <div className="space-y-4 text-sm">
          <p>
            <strong>Total Marks Possible:</strong> {assessment.totalMarks ?? 'N/A'}
          </p>
          {assessment.criteria.map((criterion: any) => (
            <div key={criterion.name} className="p-4 border border-secondary-200 rounded-md bg-white">
              <h4 className="font-semibold text-base text-secondary-800">
                {criterion.name} <span className="font-normal text-sm text-secondary-600">(Weight: {criterion.weight}%)</span>
              </h4>
              <ul className="mt-3 space-y-2">
                {[...(criterion.bands || [])]
                  .sort((a, b) => (b.max || 0) - (a.max || 0))
                  .map((band, bandIdx) => (
                    <li key={bandIdx} className="flex gap-4 items-start">
                      <span className="w-28 shrink-0 text-right font-semibold text-secondary-800 leading-snug">
                        {band.min} - {band.max} marks
                      </span>
                      <p className="text-secondary-600 leading-snug">{band.descriptor}</p>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      );
    }
    // Fallback for old freeform text rubrics
    if (assessment?.rubric) {
      return <p className="whitespace-pre-wrap">{assessment.rubric}</p>;
    }
    return <p className="text-secondary-500">No rubric information available for this assessment.</p>;
  };


  // Get primary score for display
  const primaryModelResult = essayData.modelResults 
    ? Object.values(essayData.modelResults)[0] as ModelResult | undefined
    : undefined;

  return (
    <div className="w-full space-y-6">
      {/* Header Card */}
      <Card className="border-0 shadow-md overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary to-primary/60" />
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Button 
                onClick={onBackToEssays} 
                variant="outline" 
                size="sm" 
                disabled={isProcessing}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Essays
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button 
                onClick={onRemark} 
                variant="default" 
                size="sm"
                isLoading={isProcessing}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Re-mark
              </Button>
              <Button
                onClick={() => handleDownloadWord()}
                variant="outline"
                size="sm"
                disabled={isProcessing || !hasExportableResult(essayData) || isDownloading}
                isLoading={isDownloading}
                loadingText="Exporting..."
                className="gap-2"
                title="Download a formatted feedback report (.docx — opens in Word and Google Docs)"
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Student Info */}
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{essayData.studentName}</h2>
                  <p className="text-sm text-muted-foreground">
                    {assessment?.name || 'Essay Assessment'}
                  </p>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap gap-3">
                {essayData.wordCount !== undefined && (
                  <Badge variant="secondary" className="gap-1.5 py-1 px-3">
                    <Hash className="h-3 w-3" />
                    {essayData.wordCount} words
                  </Badge>
                )}
                {essayData.modelUsed && (
                  <Badge variant="outline" className="gap-1.5 py-1 px-3">
                    <Bot className="h-3 w-3" />
                    {isAdmin ? essayData.modelUsed : 'AI Marked'}
                  </Badge>
                )}
                {showManualSections && typeof essayData.manualOverallScore === 'number' && (
                  <Badge variant="default" className="gap-1.5 py-1 px-3 bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="h-3 w-3" />
                    Manually Verified
                  </Badge>
                )}
              </div>
            </div>

            {/* Score Display */}
            {primaryModelResult && (
              <div className="flex items-center gap-4">
                <div className="text-center p-4 rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg min-w-[120px]">
                  <p className="text-xs uppercase tracking-wide font-medium opacity-90">AI Score</p>
                  <p className="text-4xl font-bold">{primaryModelResult.finalOverallScore ?? 'N/A'}</p>
                  {primaryModelResult.finalOverallBand && (
                    <p className="text-xs opacity-80 mt-1">Band {primaryModelResult.finalOverallBand}</p>
                  )}
                </div>
                {showManualSections && typeof essayData.manualOverallScore === 'number' && (
                  <div className="text-center p-4 rounded-2xl bg-secondary border-2 border-primary/20 min-w-[100px]">
                    <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground">Manual</p>
                    <p className="text-3xl font-bold text-foreground">{essayData.manualOverallScore}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assessment Question Card */}
      {assessment?.question && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              Assessment Question
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {assessment.question}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Original Essay Pages */}
      {essayData.uploadedImages && essayData.uploadedImages.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Image className="h-4 w-4 text-primary" />
              Original Essay Pages
            </CardTitle>
            <CardDescription>
              {essayData.uploadedImages.length} page{essayData.uploadedImages.length !== 1 ? 's' : ''} uploaded
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {essayData.uploadedImages.map((image: any) => (
                <div key={image.id} className="group relative">
                  <div className="aspect-[3/4] rounded-lg border bg-secondary/50 overflow-hidden">
                    <img 
                      src={image.previewUrl} 
                      alt={`Essay page ${image.pageLabel}`} 
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200" 
                    />
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-2 font-medium">
                    Page {image.pageLabel}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}



      {/* AI Marking Results & Feedback Section - Redesigned with shadcn/ui */}
      {essayData.modelResults && Object.keys(essayData.modelResults).length > 0 && (
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" />
                  AI Marking Results
                </CardTitle>
                <CardDescription>
                  Automated marking overview for this essay
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(essayData.modelResults).map(([modelId, modelResult], modelIndex) => {
              const modelConfig = MODELS[modelId];
              const typedModelResult = modelResult as ModelResult;
              const baseModelName = getModelDisplayName(modelId);
              const displayModelName = isAdmin ? baseModelName : `${baseModelName}${totalModels > 1 ? ` #${modelIndex + 1}` : ''}`;
              const lastRun = formatModelLastRun(typedModelResult.lastRunAt);
              
              return (
                <AIMarkingResultCard
                  key={modelId}
                  modelId={modelId}
                  modelResult={typedModelResult}
                  modelConfig={modelConfig}
                  displayModelName={displayModelName}
                  lastRun={lastRun}
                  isAdmin={isAdmin}
                  showManualSections={showManualSections}
                  essayData={essayData}
                  assessment={assessment}
                  isProcessing={isProcessing}
                  onDeleteModelResult={onDeleteModelResult}
                  onExportReport={isMultiModelView ? (mid: string) => handleDownloadWord(mid) : undefined}
                />
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Transcribed Essay Text Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Transcribed Essay Text
          </CardTitle>
          <CardDescription>
            Text extracted from all uploaded pages
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {essayData.transcribedText ? (
            <div className="bg-secondary/30 rounded-lg p-4 max-h-96 overflow-y-auto">
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {essayData.transcribedText}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">Could not transcribe essay text.</p>
          )}
        </CardContent>
      </Card>
      
      {/* Marking Rubric Card */}
      {assessment && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              Marking Rubric
            </CardTitle>
            <CardDescription>
              Criteria and band descriptors used for assessment
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {renderRubric()}
          </CardContent>
        </Card>
      )}

      {/* Teacher Manual Marking Section */}
      {showManualSections && (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <PenTool className="h-4 w-4 text-primary" />
                Manual Teacher Marks
              </CardTitle>
              <CardDescription>
                Enter your assessment to compare with AI marking
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              {essayData.consolidatedMarkingResults && essayData.consolidatedMarkingResults.map((criterion: any) => (
                <div key={criterion.criterionName} className="p-4 bg-secondary/30 rounded-lg border border-border space-y-3">
                  <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
                    <div className="flex-1">
                      <label htmlFor={`manual-${criterion.criterionName}`} className="text-sm font-semibold text-foreground">
                        {criterion.criterionName}
                      </label>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label htmlFor={`manual-${criterion.criterionName}`} className="text-xs font-medium text-muted-foreground">
                        Band / Range
                      </label>
                      <input
                        type="text"
                        id={`manual-${criterion.criterionName}`}
                        value={manualInputs[criterion.criterionName] ?? ''}
                        onChange={(e: any) => handleManualInputChange(criterion.criterionName, e.target.value)}
                        className="w-32 h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="e.g., 21-25"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor={`justification-${criterion.criterionName}`} className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Justification (optional)
                    </label>
                    <textarea
                      id={`justification-${criterion.criterionName}`}
                      rows={2}
                      value={manualInputs[`${criterion.criterionName}-justification`] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleManualInputChange(`${criterion.criterionName}-justification`, e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                      placeholder={`Your notes for ${criterion.criterionName}...`}
                    />
                  </div>
                </div>
              ))}
              
              {/* Overall Score */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <label htmlFor="manual-overall" className="text-base font-semibold text-foreground">
                  Overall Manual Score
                </label>
                <input
                  type="number"
                  id="manual-overall"
                  value={manualInputs['overall'] ?? ''}
                  onChange={(e) => handleManualInputChange('overall', e.target.value)}
                  className="w-24 h-10 px-3 rounded-md border border-input bg-background text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Score"
                />
              </div>
              
              {/* Teacher Feedback */}
              <div className="pt-4 border-t border-border space-y-3">
                <TextArea 
                  label="Your Feedback to Student (Optional)"
                  id="teacher-feedback"
                  value={teacherFeedback}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTeacherFeedback(e.target.value)}
                  rows={4}
                  placeholder="Enter your own feedback here. The AI can analyze it and offer suggestions."
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Add from:</span>
                  <label htmlFor="image-feedback-upload" className="inline-flex items-center gap-1.5 cursor-pointer px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors">
                    <Upload className="h-3 w-3" />
                    Image
                  </label>
                  <input id="image-feedback-upload" type="file" accept="image/*" onChange={handleFeedbackFileChange} className="hidden" disabled={isProcessingFeedbackFile} />
                  
                  <label htmlFor="camera-feedback-upload" className="inline-flex items-center gap-1.5 cursor-pointer px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors">
                    <Camera className="h-3 w-3" />
                    Camera
                  </label>
                  <input id="camera-feedback-upload" type="file" accept="image/*" capture="environment" onChange={handleFeedbackFileChange} className="hidden" disabled={isProcessingFeedbackFile} />

                  <label htmlFor="doc-feedback-upload" className="inline-flex items-center gap-1.5 cursor-pointer px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors">
                    <FileUp className="h-3 w-3" />
                    Document
                  </label>
                  <input id="doc-feedback-upload" type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,application/pdf,.txt,text/plain" onChange={handleFeedbackFileChange} className="hidden" disabled={isProcessingFeedbackFile} />

                  {isProcessingFeedbackFile && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Processing...
                    </div>
                  )}
                </div>
                {feedbackFileError && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {feedbackFileError}
                  </p>
                )}
              </div>
              
              <div className="pt-4 flex justify-end">
                <Button 
                  onClick={handleSaveManual} 
                  isLoading={isSavingManual} 
                  loadingText="Saving..." 
                  variant="default"
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Manual Marks
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* AI Variance Explanation Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-primary" />
                AI vs Manual Comparison
              </CardTitle>
              <CardDescription>
                {isMultiModelView 
                  ? "Select an AI model to compare with your manual marks"
                  : "Ask AI to explain scoring differences and analyze your feedback"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              {isMultiModelView && essayData.modelResults && (
                <div>
                  <label htmlFor="model-select" className="block text-sm font-medium text-foreground mb-2">
                    Select AI Model:
                  </label>
                  <select
                    id="model-select"
                    value={selectedModelForComparison}
                    onChange={(e) => setSelectedModelForComparison(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Choose a model...</option>
                    {Object.entries(essayData.modelResults).map(([modelId, modelResult], modelIndex) => {
                      const modelConfig = MODELS[modelId];
                      const baseModelName = getModelDisplayName(modelId);
                      const optionLabel = isAdmin
                        ? (modelConfig?.name || modelId)
                        : `${baseModelName}${totalModels > 1 ? ` #${modelIndex + 1}` : ''}`;
                      return (
                        <option key={modelId} value={modelId}>
                          {optionLabel} (Score: {(modelResult as ModelResult).finalOverallScore ?? 'N/A'})
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              
              <Button
                onClick={handleExplainVarianceClick}
                isLoading={isExplaining}
                disabled={
                  typeof essayData.manualOverallScore !== 'number' || 
                  isProcessing || 
                  isExplaining ||
                  (isMultiModelView && !selectedModelForComparison)
                }
                loadingText="Analyzing..."
                variant="default"
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Explain Variance
              </Button>
              
              {typeof essayData.manualOverallScore !== 'number' && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-3 w-3" />
                  Save manual marks first to enable variance analysis
                </p>
              )}

              {essayData.varianceExplanation && (
                <div className="mt-4 p-4 bg-secondary/30 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary" className="gap-1">
                      <Sparkles className="h-3 w-3" />
                      AI Analysis
                    </Badge>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <MarkdownRenderer text={essayData.varianceExplanation} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Footer Actions */}
      <Card className="border-0 bg-secondary/30">
        <CardContent className="py-6">
          <div className="flex justify-center items-center flex-wrap gap-3">
            <Button 
              onClick={onStartOver} 
              size="lg" 
              variant="outline" 
              disabled={isProcessing}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Mark Another Essay
            </Button>
            <Button 
              onClick={onRemark} 
              size="lg" 
              variant="default" 
              isLoading={isProcessing}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Re-mark This Essay
            </Button>
            <Button
              onClick={() => handleDownloadWord()}
              size="lg"
              variant="outline"
              disabled={isProcessing || !hasExportableResult(essayData) || isDownloading}
              isLoading={isDownloading}
              loadingText="Exporting..."
              className="gap-2"
              title="Download a formatted feedback report (.docx — opens in Word and Google Docs)"
            >
              <Download className="h-4 w-4" />
              Export Report
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultsDisplay;
